import { type FC } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePendingFriendRequests } from '@/hooks/usePendingFriendRequests';
import { ButtonLink } from '@/components/Button';
import { AppNav } from '@/components/AppNav';
import { Dropdown, DropdownItem, DropdownDivider } from '@/components/Dropdown/Dropdown';
import { Avatar } from '@/components/Avatar/Avatar';
import { NotificationBell } from '@/components/NotificationBell/NotificationBell';
import { ChevronDown, LogOut, Shield } from 'lucide-react';

/**
 * Header component props
 */
export interface HeaderProps {
  /** Optional additional className */
  className?: string;
}

/**
 * Header - logo, the four primary tabs, notifications, and the account area.
 * The avatar links to You; the small menu beside it holds only Sign out (and Admin).
 */
export const Header: FC<HeaderProps> = ({ className = '' }) => {
  const { isAuthenticated, user, logout, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const { count: pendingFriendRequests } = usePendingFriendRequests();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const friendBadge = pendingFriendRequests > 0 && (
    <span className="absolute -top-1 -right-1 inline-flex min-w-[18px] h-[18px] items-center justify-center bg-accent-primary text-text-on-accent text-xs font-bold rounded-full px-1">
      {pendingFriendRequests > 99 ? '99+' : pendingFriendRequests}
    </span>
  );

  return (
    <header className={`app-header ${className}`}>
      <div className="container-app">
        <div className="flex items-center justify-between h-12 sm:h-16">
          {/* Logo / Brand */}
          <Link
            to={isAuthenticated ? '/home' : '/'}
            className="flex items-center gap-1.5 sm:gap-2 text-accent-primary hover:text-accent-primary-bright transition-colors"
          >
            <img src="/logo.png" alt="" width={26} height={32} className="h-6 sm:h-8 w-auto" />
            <span className="font-display font-bold text-lg uppercase tracking-wide hidden sm:inline">
              Grim Dark Companion
            </span>
            <span className="font-display font-bold text-[11px] leading-[1.1] uppercase tracking-wide sm:hidden">
              Grim Dark<br />Companion
            </span>
          </Link>

          {/* Right side: Nav + User */}
          <div className="flex items-center gap-1 sm:gap-3">
            {isLoading ? (
              /* Show nothing while auth is resolving to prevent flash of Sign In */
              <div className="w-16 h-8" />
            ) : isAuthenticated ? (
              <>
                <AppNav />
                <NotificationBell />
                <Link
                  to="/you"
                  aria-label={pendingFriendRequests > 0 ? `You, ${pendingFriendRequests} friend requests` : 'You'}
                  className="relative inline-flex min-h-[44px] items-center gap-2 rounded-default px-1 sm:px-2 hover:bg-bg-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                >
                  <Avatar avatarUrl={user?.avatar_url} displayName={user?.display_name} size="sm" />
                  <span className="hidden lg:inline text-sm font-medium text-text-primary">{user?.display_name}</span>
                  {friendBadge}
                </Link>
                <Dropdown
                  align="right"
                  trigger={
                    <span aria-label="Account menu" className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-default hover:bg-bg-hover cursor-pointer">
                      <ChevronDown aria-hidden="true" size={16} className="text-text-secondary" />
                    </span>
                  }
                >
                  <div className="px-4 py-3 border-b border-border-subtle">
                    <p className="text-sm font-medium text-text-primary">{user?.display_name}</p>
                    <p className="text-xs text-text-muted">{user?.email}</p>
                  </div>
                  {user?.platform_role === 'platform_admin' && (
                    <DropdownItem icon={<Shield size={16} />} onClick={() => navigate('/admin')}>
                      Admin
                    </DropdownItem>
                  )}
                  <DropdownDivider />
                  <DropdownItem icon={<LogOut size={16} />} variant="danger" onClick={handleLogout}>
                    Sign out
                  </DropdownItem>
                </Dropdown>
              </>
            ) : (
              <ButtonLink to="/login" variant="primary" size="sm">
                Sign In
              </ButtonLink>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
